'use client'

import { ListItemLayout, LuneTheme } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import { SxProps } from '@mui/system'
import moment from 'moment'
import Image from 'next/image'
import Link from 'next/link'
import { FC, ReactNode } from 'react'

import { RenderOn } from '@/utills/DeviceUtils'

interface BlogCardProps {
    imageSrc: string
    title: ReactNode
    date?: string
    author?: { name: string; avatar: string; title: string | undefined } | undefined
    lastPublishedDate?: string
    href: string | undefined
    label?: string
    customLabel?: string | undefined
    sx?: SxProps
    onClick?: () => void
    size: 'S' | 'M' | 'L'
    description?: string
    customImgAlt?: string
}

// eslint-disable-next-line complexity
const BlogCard: FC<BlogCardProps> = ({
    imageSrc,
    href,
    date,
    lastPublishedDate,
    author,
    size = 'M',
    title,
    label,
    customLabel,
    sx,
    onClick,
    description,
    customImgAlt,
}) => {
    const { palette } = LuneTheme

    const isLarge = size === 'L'
    const isSmall = size === 'S'
    // Custom labels should be visible only for 14 days
    // re: https://linear.app/lune/issue/LUN-3751/create-fomo-labels-to-add-to-blog-previews
    const customLabelExpired = moment().diff(moment(lastPublishedDate), 'days') > 14
    const finalLabel = !customLabelExpired && customLabel ? customLabel : label

    const Label = () => (
        <>
            {label && (
                <Text
                    variant={'button'}
                    sx={{
                        padding: '4px 6px',
                        borderRadius: '6px',
                        width: 'max-content',
                        color:
                            !customLabelExpired && customLabel ? palette.Red500 : palette.Grey900,
                        backgroundColor:
                            !customLabelExpired && customLabel ? palette.Red50 : palette.Grey50,
                    }}
                >
                    {finalLabel}
                </Text>
            )}
        </>
    )

    const Date = () => (
        <>
            {date && (
                <Box sx={{ borderTop: '1px solid', pt: 1, width: 'max-content' }}>
                    <Text variant={'caption'}>{moment(date).format('MMMM D, YYYY')}</Text>
                </Box>
            )}
        </>
    )

    const Author = () =>
        author ? (
            <ListItemLayout
                sx={{
                    '.MuiListItemAvatar-root': {
                        maxWidth: '48px',
                        maxHeight: '48px',
                        borderRadius: '100%',
                        overflow: 'hidden',
                    },
                }}
                title={
                    <Text variant={'button'} sx={{ display: 'block' }}>
                        {author.name}
                    </Text>
                }
                subTitle={
                    author.title ? <Text variant={'caption'}>{author.title}</Text> : undefined
                }
                image={
                    <Image
                        src={author.avatar}
                        width={48}
                        height={48}
                        alt={`blog-card-author-${author.name}`}
                    />
                }
            />
        ) : (
            <></>
        )

    const content = (
        <>
            <RenderOn breakPoints={['md', 'lg', 'xl']}>
                <Box
                    sx={{
                        gap: isLarge ? '48px' : '24px',
                        display: 'flex',
                        alignItems: { xs: 'center', sm: 'center', md: 'flex-start' },
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'row', sm: 'row', md: isLarge ? 'row' : 'column' },
                        height: '100%',
                    }}
                >
                    <Box
                        sx={{
                            width: isLarge ? '60%' : '100%',
                            height: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                paddingBottom: {
                                    xs: '53.12%',
                                    sm: '53.12%',
                                    md: isLarge ? '53.04%' : '53.12%',
                                },
                                height: 0,
                                minHeight: '100%',
                                width: '100%',
                                minWidth: '100%',
                                maxWidth: '100%',
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: '20px',
                                mb: { xs: 0, sm: 0, md: 3 },
                                flex: '1 1 auto',
                                img: {
                                    position: 'absolute',
                                    top: 0,
                                    width: 'auto',
                                    height: '100%',
                                    zIndex: -1,
                                    right: '-50%',
                                    left: '-50%',
                                    margin: 'auto',
                                },
                            }}
                        >
                            <Image
                                src={imageSrc}
                                alt={customImgAlt ?? `blog-${title}-image`}
                                {...{ fill: true, style: { objectFit: 'cover' } }}
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            flex: '1 1 auto',
                            width: isLarge ? '40%' : '100%',
                            display: 'flex',
                            gap: { xs: 2, sm: 2, md: 3 },
                            flexDirection: 'column',
                            height: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                flex: '1 1 auto',
                                display: 'flex',
                                gap: { xs: 2, sm: 2, md: 3 },
                                flexDirection: 'column',
                            }}
                        >
                            <Label />
                            <Text
                                className={'blog-post-title'}
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    lineClamp: isLarge ? '4' : '2',
                                    '-webkit-line-clamp': isLarge ? '4' : '2',
                                    '-webkit-box-orient': 'vertical',
                                }}
                                variant={isLarge ? 'h4' : isSmall ? 'h6' : 'h5'}
                            >
                                {title}
                            </Text>
                            {description && <Text variant={'body1'}>{description}</Text>}
                            {author && <Author />}
                        </Box>
                        <Box>{!isSmall && <Date />}</Box>
                    </Box>
                </Box>
            </RenderOn>

            <RenderOn breakPoints={['xs', 'sm']}>
                <Box
                    sx={{
                        gap: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            paddingBottom: '53.12%',
                            height: 0,
                            width: '100%',
                            minWidth: '100%',
                            maxWidth: '100%',
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: '20px',
                            mb: { xs: 0, sm: 0, md: 3 },
                            flex: '1 1 auto',
                            img: {
                                position: 'absolute',
                                top: 0,
                                width: 'auto',
                                height: '100%',
                                zIndex: -1,
                                right: '-50%',
                                left: '-50%',
                                margin: 'auto',
                            },
                        }}
                    >
                        <Image
                            src={imageSrc}
                            alt={`blog-${title}-image`}
                            {...{ fill: true, style: { objectFit: 'cover' } }}
                        />
                    </Box>
                    <Box
                        sx={{
                            flex: '1 1 auto',
                            display: 'flex',
                            gap: 2,
                            flexDirection: 'column',
                            width: '100%',
                        }}
                    >
                        <Label />
                        <Text className={'blog-post-title'} variant={'h6'}>
                            {title}
                        </Text>
                        {description && <Text variant={'body2'}>{description}</Text>}
                        {author && <Author />}
                        <Date />
                    </Box>
                </Box>
            </RenderOn>
        </>
    )

    return (
        <Box
            sx={{
                display: 'flex',
                ...(href && {
                    '&:hover, &:focus': {
                        '.blog-post-title': {
                            textDecoration: 'underline',
                        },
                    },
                }),
                ...sx,
            }}
        >
            {href ? (
                <Link
                    href={href}
                    style={{
                        width: '100%',
                        color: palette.Grey900,
                        textDecoration: 'none',
                    }}
                    onClick={onClick}
                >
                    {content}
                </Link>
            ) : (
                <Box
                    style={{
                        width: '100%',
                        color: palette.Grey900,
                        textDecoration: 'none',
                    }}
                >
                    {content}
                </Box>
            )}
        </Box>
    )
}

export default BlogCard
